import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Stack
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { addPermission } from "../../../store/actions/rolePermissionActions";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router-dom";

const levels = ["NO_ACCESS", "VIEW_ONLY", "READ_WRITE"];

const PermissionManager = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const permissionData = location?.state?.permissionData;

  const [permissions, setPermissions] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  //  Build category-only list
  useEffect(() => {
    if (permissionData?.data?.permissions) {
      const categories = permissionData.data.permissions.map((item) => ({
        id: item.id,
        categoryLabel: item.categoryLabel,
        category: item.category,
        permissionLevel: item.permissionLevel || "NO_ACCESS",
      }));

      setPermissions(categories);
      setInitialPermissions(JSON.parse(JSON.stringify(categories)));
    }
  }, [permissionData]);

  //  Handle checkbox change (radio behavior)
  const handleChange = (id, level) => {
    const updated = permissions.map((p) =>
      p.id === id ? { ...p, permissionLevel: level } : p
    );
    setPermissions(updated);
  };

  //  Submit API
  const handleSubmit = async () => {
    const payload = permissions.map((p) => ({
      permissionId: p.id,
      permissionLevel: p.permissionLevel,
    }));

    try {
      await dispatch(
        addPermission({
          roleId: permissionData?.details?.id,
          permissions: payload,
        })
      );

      setInitialPermissions(JSON.parse(JSON.stringify(permissions)));

      setSnack({
        open: true,
        message: "Permissions updated successfully!",
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        message: "Failed to update permissions.",
        severity: "error",
      });
    }
  };

  const handleReset = () => {
    setPermissions(JSON.parse(JSON.stringify(initialPermissions)));
  };

  const handleclose = () => {
    if (JSON.stringify(permissions) !== JSON.stringify(initialPermissions)) {
      const shouldStay = window.confirm(`
You have unsaved changes.

Click OK to stay on this page.
Click Cancel to go back.`);
      if (shouldStay) return;
    }
    history.goBack();
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#fff", }}>
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          py: 1,
          borderBottom: "1px solid #eee"
        }}
      >

        <Typography variant="h5" fontWeight={600} color="#0911A7" sx={{ flex: 1, textAlign: 'center' }}>
          Manage Permissions
        </Typography>
        <IconButton onClick={handleclose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Categories */}
      <Grid container spacing={3}>
        {permissions.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
            <Box sx={{ border: "1px solid #ddd", p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {item.categoryLabel}
              </Typography>

              {levels.map((level) => (
                <Box key={level} sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={item.permissionLevel === level}
                    onChange={() => handleChange(item.id, level)}
                  />
                  <Typography>{level}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Footer */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          backgroundColor: "#f5f5f5",
          py: 2,
          borderTop: "1px solid #eee",
          mt: 3
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            JSON.stringify(permissions) === JSON.stringify(initialPermissions)
          }
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={
            JSON.stringify(permissions) === JSON.stringify(initialPermissions)
          }
        >
          Reset
        </Button>
      </Stack>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionManager;